/* istanbul ignore file */
import { resourceAllocationTypesEnum } from '@linearb/linjs-common';
import { PM_PROVIDERS } from 'common/constants/constants';
import {
  azureBoardsViewer,
  jiraViewer
} from 'components/hooks/PM/pm-provider-viewers';
import { reduce, startCase } from 'lodash';

export const MAIN_UI_TYPES = {
  FTE_ALLOCATION: 'fte_allocation', // Donut Chart
  ALLOCATION_OVER_TIME: 'allocation_over_time', // Column Chart
  AGGREGATED_TABLE: 'aggregated_table'
};

export const TOOLTIP_VIEWS = {
  ISSUES_LIST: 'issues_list',
  INVESTMENT_BREAKDOWN: 'investment_breakdown',
  ASSIGNEES_LIST: 'assignees_list'
};

export const generateResourceTypeFromIssueType = (issueType, pmProvider) => ({
  key: issueType,
  name: issueType,
  type: resourceAllocationTypesEnum.DYNAMIC_ISSUE_TYPE,
  issueType,
  singleName: issueType,
  displayIn: [
    MAIN_UI_TYPES.ALLOCATION_OVER_TIME,
    MAIN_UI_TYPES.AGGREGATED_TABLE
  ],
  tooltipViews: [TOOLTIP_VIEWS.INVESTMENT_BREAKDOWN],
  isNative: true,
  isTogglable: true,
  pmProvider
});

export const RESOURCE_TYPES = {
  JIRA_PROJECTS: {
    key: 'JIRA_PROJECTS',
    name: 'Projects',
    type: resourceAllocationTypesEnum.PROJECT,
    singleName: 'project',
    displayIn: [
      MAIN_UI_TYPES.ALLOCATION_OVER_TIME,
      MAIN_UI_TYPES.AGGREGATED_TABLE
    ],
    tooltipViews: [TOOLTIP_VIEWS.INVESTMENT_BREAKDOWN],
    isTogglable: false,
    pmProvider: PM_PROVIDERS.JIRA,
    isNative: true
  },
  JIRA_INITIATIVES: {
    key: 'JIRA_INITIATIVES',
    name: startCase(jiraViewer.oldIssueParent2.plural),
    type: resourceAllocationTypesEnum.INITIATIVE,
    singleName: jiraViewer.oldIssueParent2.single,
    displayIn: [
      MAIN_UI_TYPES.ALLOCATION_OVER_TIME,
      MAIN_UI_TYPES.AGGREGATED_TABLE
    ],
    tooltipViews: [TOOLTIP_VIEWS.ISSUES_LIST],
    isTogglable: true,
    pmProvider: PM_PROVIDERS.JIRA,
    isNative: true
  },
  JIRA_EPICS: {
    key: 'JIRA_EPICS',
    name: startCase(jiraViewer.oldIssueParent1.plural),
    type: resourceAllocationTypesEnum.EPIC,
    singleName: jiraViewer.oldIssueParent1.single,
    displayIn: [
      MAIN_UI_TYPES.ALLOCATION_OVER_TIME,
      MAIN_UI_TYPES.AGGREGATED_TABLE
    ],
    tooltipViews: [TOOLTIP_VIEWS.INVESTMENT_BREAKDOWN],
    isTogglable: true,
    pmProvider: PM_PROVIDERS.JIRA,
    isNative: true
  },
  JIRA_ISSUE_TYPES: {
    key: 'JIRA_ISSUE_TYPES',
    name: startCase(jiraViewer.issueType.plural),
    type: resourceAllocationTypesEnum.ISSUE_TYPE,
    singleName: jiraViewer.issueType.single,
    displayIn: [
      MAIN_UI_TYPES.FTE_ALLOCATION,
      MAIN_UI_TYPES.ALLOCATION_OVER_TIME
    ],
    tooltipViews: [],
    isTogglable: false,
    pmProvider: PM_PROVIDERS.JIRA,
    isNative: true
  },
  AZURE_PROJECTS: {
    key: 'AZURE_PROJECTS',
    name: 'Projects',
    type: resourceAllocationTypesEnum.PROJECT,
    singleName: 'project',
    displayIn: [
      MAIN_UI_TYPES.ALLOCATION_OVER_TIME,
      MAIN_UI_TYPES.AGGREGATED_TABLE
    ],
    tooltipViews: [TOOLTIP_VIEWS.INVESTMENT_BREAKDOWN],
    isTogglable: false,
    pmProvider: PM_PROVIDERS.AZURE_BOARDS,
    isNative: true
  },
  AZURE_WORK_ITEMS: {
    key: 'AZURE_WORK_ITEMS',
    name: startCase(azureBoardsViewer.issueType.plural),
    type: resourceAllocationTypesEnum.ISSUE_TYPE,
    singleName: azureBoardsViewer.issueType.single,
    displayIn: [
      MAIN_UI_TYPES.FTE_ALLOCATION,
      MAIN_UI_TYPES.ALLOCATION_OVER_TIME
    ],
    tooltipViews: [],
    isTogglable: false,
    pmProvider: PM_PROVIDERS.AZURE_BOARDS,
    isNative: true
  },
  AZURE_EPICS: {
    key: 'AZURE_EPICS',
    name: startCase(azureBoardsViewer.oldIssueParent2.plural),
    type: resourceAllocationTypesEnum.EPIC,
    singleName: azureBoardsViewer.oldIssueParent2.single,
    displayIn: [
      MAIN_UI_TYPES.ALLOCATION_OVER_TIME,
      MAIN_UI_TYPES.AGGREGATED_TABLE
    ],
    tooltipViews: [TOOLTIP_VIEWS.ISSUES_LIST],
    isTogglable: true,
    pmProvider: PM_PROVIDERS.AZURE_BOARDS,
    isNative: true
  },
  AZURE_FEATURES: {
    key: 'AZURE_FEATURES',
    name: startCase(azureBoardsViewer.oldIssueParent1.plural),
    type: resourceAllocationTypesEnum.FEATURE,
    singleName: azureBoardsViewer.oldIssueParent1.single,
    displayIn: [
      MAIN_UI_TYPES.ALLOCATION_OVER_TIME,
      MAIN_UI_TYPES.AGGREGATED_TABLE
    ],
    tooltipViews: [TOOLTIP_VIEWS.INVESTMENT_BREAKDOWN],
    isTogglable: true,
    pmProvider: PM_PROVIDERS.AZURE_BOARDS,
    isNative: true
  }
};

export const RA_ADDITIONAL_TYPES = {
  CUSTOM_FIELD: 'custom_field',
  LABEL: 'label',
  LABEL_GROUP: 'label_group'
};

export const generateResourceTypeFromAdditionalType = ({
  additionalType,
  pmProvider = null
}) => {
  const mapAdditionalTypesExtra = {
    [RA_ADDITIONAL_TYPES.CUSTOM_FIELD]: {
      extraParams: {
        id: additionalType.id,
        custom_field_id: additionalType.id
      }
    },
    [RA_ADDITIONAL_TYPES.LABEL_GROUP]: {
      extraParams: {
        labels: additionalType.labels?.map(label => label?.id)
      },
      type: RA_ADDITIONAL_TYPES.LABEL
    }
  };

  const newResourceType = {
    key: additionalType.id,
    name: additionalType.title,
    type: additionalType.type,
    displayIn: [
      MAIN_UI_TYPES.FTE_ALLOCATION,
      MAIN_UI_TYPES.ALLOCATION_OVER_TIME,
      MAIN_UI_TYPES.AGGREGATED_TABLE
    ],
    tooltipViews: [TOOLTIP_VIEWS.INVESTMENT_BREAKDOWN],
    pmProvider,
    ...mapAdditionalTypesExtra[additionalType.type]
  };

  return newResourceType;
};

export const LABELS_TYPES = {
  AZURE_TAGS: {
    key: 'AZURE_TAGS',
    name: startCase(azureBoardsViewer.label.plural),
    type: resourceAllocationTypesEnum.LABEL,
    singleName: azureBoardsViewer.label.single,
    displayIn: [
      MAIN_UI_TYPES.FTE_ALLOCATION,
      MAIN_UI_TYPES.ALLOCATION_OVER_TIME,
      MAIN_UI_TYPES.AGGREGATED_TABLE
    ],
    tooltipViews: [TOOLTIP_VIEWS.INVESTMENT_BREAKDOWN],
    isTogglable: true,
    pmProvider: PM_PROVIDERS.AZURE_BOARDS,
    isNative: true,
    setExtraParams(labels) {
      this.extraParams = {
        ...(labels?.length && { labels })
      };
    }
  },
  JIRA_LABELS: {
    key: 'JIRA_LABELS',
    name: startCase(jiraViewer.label.plural),
    type: resourceAllocationTypesEnum.LABEL,
    singleName: jiraViewer.label.single,
    displayIn: [
      MAIN_UI_TYPES.FTE_ALLOCATION,
      MAIN_UI_TYPES.ALLOCATION_OVER_TIME,
      MAIN_UI_TYPES.AGGREGATED_TABLE
    ],
    tooltipViews: [TOOLTIP_VIEWS.INVESTMENT_BREAKDOWN],
    isTogglable: true,
    pmProvider: PM_PROVIDERS.JIRA,
    isNative: true,
    setExtraParams(labels) {
      this.extraParams = {
        ...(labels?.length && { labels })
      };
    }
  }
};

export const getLabelsResourceTypeByPmProvider = pmProvider => {
  const labelResourceTypeMapper = {
    [PM_PROVIDERS.JIRA]: LABELS_TYPES.JIRA_LABELS,
    [PM_PROVIDERS.AZURE_BOARDS]: LABELS_TYPES.AZURE_TAGS
  };
  return labelResourceTypeMapper[pmProvider];
};

export const getResourceTypesByPmSensorId = (resourceTypes, pmProvider) =>
  reduce(
    resourceTypes,
    (acc, resourceType) => ({
      ...acc,
      ...((!resourceType.pmProvider ||
        resourceType.pmProvider === pmProvider) && {
        [resourceType.key]: resourceType
      })
    }),
    {}
  );

export const getResourceTypeDependencies = resourceType => [
  resourceType,
  // to avoid using specific prop of the extraParams, use a string has a dependency
  JSON.stringify(resourceType.extraParams)
];
